import { DragEvent, ReactNode, useRef, useState } from 'react';
import styled from 'styled-components';
import { colors, dashedBlue2, r8, space } from '@ardoq/design-tokens';
import { Icon, IconName } from '@ardoq/icons';
import { FlexBox } from '@ardoq/layout';
import { Text } from '@ardoq/typography';
import { t, type I18nLocale } from '@ardoq/i18n';

const eventHasFiles = (event: DragEvent): boolean =>
  Array.from(event.dataTransfer?.types ?? []).includes('Files');

export type FileDropZoneProps = {
  i18nLocale: I18nLocale;
  onDropFiles?: (files: File[]) => void;
  children: ReactNode;
};

export const FileDropZone = ({
  i18nLocale,
  onDropFiles,
  children,
}: FileDropZoneProps) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [isDraggingFiles, setIsDraggingFiles] = useState(false);

  if (!onDropFiles) {
    return <>{children}</>;
  }

  const isRelatedTargetOutside = (
    relatedTarget: EventTarget | null
  ): boolean => {
    const node = relatedTarget instanceof Node ? relatedTarget : null;
    return !node || !wrapperRef.current?.contains(node);
  };

  const handleDragEnter = (event: DragEvent<HTMLDivElement>) => {
    if (!eventHasFiles(event) || !isRelatedTargetOutside(event.relatedTarget)) {
      return;
    }
    setIsDraggingFiles(true);
  };

  const handleDragLeave = (event: DragEvent<HTMLDivElement>) => {
    if (isRelatedTargetOutside(event.relatedTarget)) {
      setIsDraggingFiles(false);
    }
  };

  const handleDragOver = (event: DragEvent<HTMLDivElement>) => {
    if (eventHasFiles(event)) {
      event.preventDefault();
    }
  };

  const handleDrop = (event: DragEvent<HTMLDivElement>) => {
    setIsDraggingFiles(false);
    if (!eventHasFiles(event)) return;
    event.preventDefault();
    onDropFiles(Array.from(event.dataTransfer.files));
  };

  return (
    <Wrapper
      ref={wrapperRef}
      onDragEnter={handleDragEnter}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      {children}
      {isDraggingFiles && (
        <DropOverlay
          flexDirection="column"
          align="center"
          justify="center"
          gap="xsmall"
        >
          <Icon iconName={IconName.ATTACHMENT} color={colors.blue600} />
          <Text variant="text2">
            {t(
              i18nLocale,
              'assistant:documents.dropFilesHere',
              'Drop files here'
            )}
          </Text>
        </DropOverlay>
      )}
    </Wrapper>
  );
};

const Wrapper = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  min-height: 0;
`;

const DropOverlay = styled(FlexBox)`
  position: absolute;
  inset: ${space.small};
  z-index: 2;
  border: ${dashedBlue2};
  border-radius: ${r8};
  background-color: ${colors.surfaceActive};
  pointer-events: none;
`;
